import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  CreditCard,
  Shield,
  AlertCircle,
  Clock,
  FileText,
  Heart,
  DollarSign,
  Activity,
  CheckCircle,
  XCircle,
  UserCheck,
  Send,
  RefreshCw,
  Key,
  Eye,
  EyeOff,
  AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../../App';
import { format } from 'date-fns';

const MemberDetailDialog = ({ member, isOpen, onClose }) => {
  const [memberDetails, setMemberDetails] = useState(null);
  const [plan, setPlan] = useState(null);
  const [payments, setPayments] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [resetPasswordForm, setResetPasswordForm] = useState({
    manualPassword: '',
    useManualPassword: false,
    sendEmail: true
  });
  const [resettingPassword, setResettingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Handle password reset for patient
  const handleResetPassword = async () => {
    if (!memberDetails?.user_id) {
      toast.error('This member does not have a user account');
      return;
    }

    if (resetPasswordForm.useManualPassword && resetPasswordForm.manualPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setResettingPassword(true);
    try {
      const response = await axios.post(`${API}/auth/admin/reset-password`, {
        user_id: memberDetails.user_id,
        new_password: resetPasswordForm.useManualPassword ? resetPasswordForm.manualPassword : null,
        send_email: resetPasswordForm.sendEmail
      });

      if (response.data.success) {
        if (response.data.is_temporary && response.data.temporary_password) {
          toast.success(
            <div>
              <p className="font-semibold">Password reset successfully!</p>
              {response.data.email_sent ? (
                <p className="text-sm">Email sent with new credentials</p>
              ) : (
                <div className="mt-2 p-2 bg-gray-100 rounded text-sm">
                  <p className="font-medium">Temporary Password:</p>
                  <code className="text-green-600 font-mono">{response.data.temporary_password}</code>
                </div>
              )}
            </div>,
            { duration: 15000 }
          );
        } else {
          toast.success('Password reset successfully!' + (response.data.email_sent ? ' Email sent.' : ''));
        }
        setResetPasswordModalOpen(false);
        setResetPasswordForm({ manualPassword: '', useManualPassword: false, sendEmail: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  // Handle resend membership email
  const handleResendMembershipEmail = async () => {
    if (sendingEmail || !member?.id) return;
    
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API}/members/${member.id}/resend-membership-email`);
      
      if (response.data.success) {
        toast.success(response.data.message || 'Email sent successfully');
      }
    } catch (error) {
      console.error('Error sending membership email:', error);
      const errorMessage = error.response?.data?.detail || 'Error sending email';
      toast.error(errorMessage);
    } finally {
      setSendingEmail(false);
    }
  };

  // Handle View as Patient - Impersonate patient to help them
  const handleViewAsPatient = async () => {
    if (impersonating || !member) return;
    
    setImpersonating(true);
    try {
      const response = await axios.post(`${API}/clinic/impersonate-patient`, {
        member_id: member.id,
        reason: 'Admin assisting patient from member details'
      });
      
      if (response.data.success) {
        // Store impersonation data
        localStorage.setItem('patient_impersonation_data', JSON.stringify({
          type: 'patient_impersonation',
          impersonated_patient: response.data.impersonated_patient,
          original_staff_id: response.data.original_staff_id,
          session_id: response.data.session_id,
          expires_at: response.data.expires_at,
          return_url: response.data.return_url
        }));
        
        // Store original user and token for restoration
        localStorage.setItem('original_admin_token', localStorage.getItem('token'));
        localStorage.setItem('original_admin_user', localStorage.getItem('user'));
        
        // Update token and user to impersonated patient
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify({
          id: response.data.impersonated_patient.id,
          email: response.data.impersonated_patient.email,
          full_name: response.data.impersonated_patient.full_name,
          role: response.data.impersonated_patient.role,
          clinic_id: response.data.impersonated_patient.clinic_id
        }));
        
        toast.success(response.data.message);
        
        // Redirect to patient dashboard
        window.location.href = '/patient';
      }
    } catch (error) {
      console.error('Error starting patient view:', error);
      const errorMessage = error.response?.data?.detail || 'Unable to view as patient';
      toast.error(errorMessage);
    } finally {
      setImpersonating(false);
    }
  };


  useEffect(() => {
    if (isOpen && member?.id) {
      fetchMemberDetails();
    }
  }, [isOpen, member?.id]);

  const fetchMemberDetails = async () => {
    setLoading(true);
    try {
      // Fetch additional member data
      const [paymentsRes, appointmentsRes] = await Promise.all([
        axios.get(`${API}/payments/member/${member.id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/appointments/member/${member.id}`).catch(() => ({ data: [] }))
      ]);

      // Fetch plan if member has one
      if (member.membership_plan_id) {
        try {
          const planRes = await axios.get(`${API}/plans/${member.membership_plan_id}`);
          setPlan(planRes.data);
        } catch (e) {
          setPlan(null);
        }
      }

      setPayments(paymentsRes.data || []);
      setAppointments(appointmentsRes.data || []);
      setMemberDetails(member);
    } catch (error) {
      console.error('Error fetching member details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString, defaultText = 'N/A') => {
    if (!dateString) return defaultText;
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime()) || date.getFullYear() < 2000) {
        return defaultText;
      }
      return format(date, 'PPP');
    } catch {
      return defaultText;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'inactive': return 'bg-gray-500';
      case 'expired': return 'bg-red-500';
      case 'pending': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const totalPayments = payments
    .filter(p => p.payment_status === 'completed')
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  const upcomingAppointments = appointments.filter(a => 
    new Date(a.appointment_date) > new Date() && 
    (a.status === 'scheduled' || a.status === 'confirmed')
  ).length;

  if (!member) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-blue-600 flex items-center justify-center text-white text-xl font-bold">
                {member.patient_id?.charAt(0) || member.full_name?.charAt(0) || 'M'}
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {member.full_name || member.user?.full_name || member.patient_id}
                </h2>
                <p className="text-sm text-gray-500">{member.patient_id}</p>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              onClick={handleViewAsPatient}
              disabled={impersonating}
              data-testid="view-as-patient-detail-btn"
              title="View patient portal as this patient"
            >
              <UserCheck className="h-4 w-4 mr-1" />
              {impersonating ? 'Loading...' : 'View as Patient'}
            </Button>
          </DialogTitle>
          <DialogDescription>
            Complete member information
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          </div>
        ) : (
          <Tabs defaultValue="profile" className="mt-4">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="profile">Profile</TabsTrigger>
              <TabsTrigger value="membership">Membership</TabsTrigger>
              <TabsTrigger value="payments">Payments</TabsTrigger>
              <TabsTrigger value="appointments">Appointments</TabsTrigger>
            </TabsList>

            {/* Profile Tab */}
            <TabsContent value="profile" className="mt-4 space-y-4">
              {/* Status Bar */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-4">
                  <Badge className={`${getStatusColor(member.membership_status)} text-white`}>
                    {member.membership_status === 'active' ? 'Active' : 
                     member.membership_status === 'inactive' ? 'Inactive' : 
                     member.membership_status === 'expired' ? 'Expired' : member.membership_status}
                  </Badge>
                  {member.subscription_status && (
                    <Badge variant="outline">
                      Subscription: {member.subscription_status}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-gray-500">
                  Member since: {formatDate(member.created_at)}
                </p>
              </div>

              {/* Personal Information */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="h-5 w-5 text-teal-600" />
                    Personal Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-sm text-gray-500 flex items-center gap-2">
                        <User className="h-4 w-4" /> Full Name
                      </p>
                      <p className="font-medium">{member.full_name || member.user?.full_name || 'N/A'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-gray-500 flex items-center gap-2">
                        <Mail className="h-4 w-4" /> Email
                      </p>
                      <p className="font-medium">{member.email || member.user?.email || 'N/A'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-gray-500 flex items-center gap-2">
                        <Phone className="h-4 w-4" /> Phone
                      </p>
                      <p className="font-medium">{member.phone || 'N/A'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-gray-500 flex items-center gap-2">
                        <Calendar className="h-4 w-4" /> Date of Birth
                      </p>
                      <p className="font-medium">
                        {formatDate(member.date_of_birth)}
                        {member.date_of_birth && ` (${calculateAge(member.date_of_birth)} years old)`}
                      </p>
                    </div>
                    <div className="space-y-1 col-span-2">
                      <p className="text-sm text-gray-500 flex items-center gap-2">
                        <MapPin className="h-4 w-4" /> Address
                      </p>
                      <p className="font-medium">{member.address || 'N/A'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Emergency Contact */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-red-500" />
                    Emergency Contact
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-sm text-gray-500">Name</p>
                      <p className="font-medium">{member.emergency_contact || 'N/A'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="font-medium">{member.emergency_phone || 'N/A'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Medical Notes */}
              {member.medical_notes && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="h-5 w-5 text-blue-500" />
                      Medical Notes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-700 whitespace-pre-wrap">{member.medical_notes}</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Membership Tab */}
            <TabsContent value="membership" className="mt-4 space-y-4">
              {plan ? (
                <>
                  <Card className="border-2 border-teal-200">
                    <CardHeader className="pb-3 bg-gradient-to-r from-teal-50 to-blue-50">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Shield className="h-5 w-5 text-teal-600" />
                          {plan.name}
                        </CardTitle>
                        <Badge className={getStatusColor(member.membership_status)}>
                          {member.membership_status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-4">
                      <p className="text-gray-600 mb-4">{plan.description}</p>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <DollarSign className="h-6 w-6 text-green-600 mx-auto mb-1" />
                          <p className="text-2xl font-bold text-gray-900">${plan.price}</p>
                          <p className="text-xs text-gray-500">Price</p>
                        </div>
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <Clock className="h-6 w-6 text-blue-600 mx-auto mb-1" />
                          <p className="text-2xl font-bold text-gray-900">{plan.duration_months}</p>
                          <p className="text-xs text-gray-500">Months</p>
                        </div>
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <Calendar className="h-6 w-6 text-teal-600 mx-auto mb-1" />
                          <p className="text-sm font-bold text-gray-900">{formatDate(member.membership_start_date, 'Pending')}</p>
                          <p className="text-xs text-gray-500">Start</p>
                        </div>
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <Calendar className="h-6 w-6 text-red-500 mx-auto mb-1" />
                          <p className="text-sm font-bold text-gray-900">{formatDate(member.membership_end_date, 'Pending')}</p>
                          <p className="text-xs text-gray-500">Expiration</p>
                        </div>
                      </div>

                      {plan.benefits && plan.benefits.length > 0 && (
                        <div className="mt-4">
                          <p className="font-medium text-gray-900 mb-2">Included Benefits:</p>
                          <ul className="grid grid-cols-2 gap-2">
                            {plan.benefits.map((benefit, index) => (
                              <li key={index} className="flex items-center gap-2 text-sm text-gray-600">
                                <CheckCircle className="h-4 w-4 text-green-500" />
                                {benefit}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Resend Membership Email Button */}
                      {member.membership_status === 'active' && (
                        <div className="mt-6 pt-4 border-t border-gray-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-700">Patient Communication</p>
                              <p className="text-xs text-gray-500">Resend confirmation email with member card</p>
                            </div>
                            <Button
                              onClick={handleResendMembershipEmail}
                              disabled={sendingEmail}
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-2"
                              data-testid="resend-membership-email-btn"
                            >
                              {sendingEmail ? (
                                <>
                                  <RefreshCw className="h-4 w-4 animate-spin" />
                                  Sending...
                                </>
                              ) : (
                                <>
                                  <Send className="h-4 w-4" />
                                  Resend Email
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Reset Password Button */}
                      {memberDetails?.user_id && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-medium text-gray-700">Account Security</p>
                              <p className="text-xs text-gray-500">Reset patient's login password</p>
                            </div>
                            <Button
                              onClick={() => setResetPasswordModalOpen(true)}
                              variant="outline"
                              size="sm"
                              className="flex items-center gap-2 text-orange-600 border-orange-300 hover:bg-orange-50"
                              data-testid="reset-patient-password-btn"
                            >
                              <Key className="h-4 w-4" />
                              Reset Password
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Subscription Details */}
                  {member.stripe_subscription_id && (
                    <Card>
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <CreditCard className="h-5 w-5 text-purple-600" />
                          Subscription Details
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-gray-500">Subscription Status</p>
                            <Badge variant="outline" className="mt-1">
                              {member.subscription_status || 'N/A'}
                            </Badge>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Cancel at Period End</p>
                            <p className="font-medium">
                              {member.subscription_cancel_at_period_end ? 'Yes' : 'No'}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Enrollment Fee Paid</p>
                            <p className="font-medium flex items-center gap-1">
                              {member.enrollment_fee_paid ? (
                                <><CheckCircle className="h-4 w-4 text-green-500" /> Yes</>
                              ) : (
                                <><XCircle className="h-4 w-4 text-red-500" /> No</>
                              )}
                            </p>
                          </div>
                          {member.enrollment_fee_paid_at && (
                            <div>
                              <p className="text-sm text-gray-500">Enrollment Payment Date</p>
                              <p className="font-medium">{formatDate(member.enrollment_fee_paid_at)}</p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Shield className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No membership plan assigned</p>
                    <p className="text-sm text-gray-400 mt-1">Assign a plan to this member</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Payments Tab */}
            <TabsContent value="payments" className="mt-4 space-y-4">
              {/* Payment Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-gray-500">Total Paid</p>
                        <p className="text-xl font-bold text-green-600">${totalPayments.toFixed(2)}</p>
                      </div>
                      <DollarSign className="h-8 w-8 text-green-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-gray-500">Transactions</p>
                        <p className="text-xl font-bold text-blue-600">{payments.length}</p>
                      </div>
                      <CreditCard className="h-8 w-8 text-blue-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-gray-500">Last Payment</p>
                        <p className="text-sm font-bold text-gray-900">
                          {payments.length > 0 ? formatDate(payments[0]?.created_at) : 'N/A'}
                        </p>
                      </div>
                      <Calendar className="h-8 w-8 text-gray-200" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Payment History */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Payment History</CardTitle>
                </CardHeader>
                <CardContent>
                  {payments.length === 0 ? (
                    <div className="text-center py-8">
                      <DollarSign className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No payments recorded</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {payments.map((payment) => (
                        <div key={payment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              payment.payment_status === 'completed' ? 'bg-green-100' : 'bg-yellow-100'
                            }`}>
                              {payment.payment_status === 'completed' ? (
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              ) : (
                                <Clock className="h-4 w-4 text-yellow-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">${payment.amount?.toFixed(2)}</p>
                              <p className="text-xs text-gray-500">
                                {formatDate(payment.created_at)} • {payment.payment_method || 'N/A'}
                              </p>
                            </div>
                          </div>
                          <Badge variant={payment.payment_status === 'completed' ? 'default' : 'secondary'}>
                            {payment.payment_status === 'completed' ? 'Completed' : 
                             payment.payment_status === 'pending' ? 'Pending' : payment.payment_status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Appointments Tab */}
            <TabsContent value="appointments" className="mt-4 space-y-4">
              {/* Appointments Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-gray-500">Total Appointments</p>
                        <p className="text-xl font-bold text-gray-900">{appointments.length}</p>
                      </div>
                      <Calendar className="h-8 w-8 text-gray-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-gray-500">Upcoming</p>
                        <p className="text-xl font-bold text-blue-600">{upcomingAppointments}</p>
                      </div>
                      <Clock className="h-8 w-8 text-blue-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-gray-500">Completed</p>
                        <p className="text-xl font-bold text-green-600">
                          {appointments.filter(a => a.status === 'completed').length}
                        </p>
                      </div>
                      <CheckCircle className="h-8 w-8 text-green-200" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Appointments List */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Appointments History</CardTitle>
                </CardHeader>
                <CardContent>
                  {appointments.length === 0 ? (
                    <div className="text-center py-8">
                      <Calendar className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No appointments recorded</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {appointments.map((apt) => {
                        const aptDate = new Date(apt.appointment_date);
                        const isPast = aptDate < new Date();
                        
                        return (
                          <div key={apt.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                apt.status === 'completed' ? 'bg-green-100' :
                                apt.status === 'cancelled' ? 'bg-red-100' :
                                isPast ? 'bg-gray-100' : 'bg-blue-100'
                              }`}>
                                {apt.status === 'completed' ? (
                                  <CheckCircle className="h-4 w-4 text-green-600" />
                                ) : apt.status === 'cancelled' ? (
                                  <XCircle className="h-4 w-4 text-red-600" />
                                ) : (
                                  <Calendar className="h-4 w-4 text-blue-600" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900 capitalize">
                                  {apt.appointment_type?.replace('-', ' ') || 'Appointment'}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {format(aptDate, 'PPP')} • {format(aptDate, 'h:mm a')}
                                </p>
                              </div>
                            </div>
                            <Badge variant={
                              apt.status === 'completed' ? 'default' :
                              apt.status === 'cancelled' ? 'destructive' :
                              apt.status === 'confirmed' ? 'default' : 'secondary'
                            }>
                              {apt.status === 'completed' ? 'Completed' :
                               apt.status === 'cancelled' ? 'Cancelled' :
                               apt.status === 'confirmed' ? 'Confirmed' :
                               apt.status === 'scheduled' ? 'Scheduled' : apt.status}
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>

      {/* Reset Password Modal */}
      <Dialog open={resetPasswordModalOpen} onOpenChange={setResetPasswordModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-orange-600" />
              Reset Patient Password
            </DialogTitle>
            <DialogDescription>
              Reset password for <span className="font-semibold">{memberDetails?.full_name}</span>
              <br />
              <span className="text-xs text-gray-500">{memberDetails?.email}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Password Type Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Password Type</Label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50">
                  <input
                    type="radio"
                    name="passwordType"
                    checked={!resetPasswordForm.useManualPassword}
                    onChange={() => setResetPasswordForm(prev => ({ ...prev, useManualPassword: false, manualPassword: '' }))}
                    className="w-4 h-4 text-orange-600"
                  />
                  <div>
                    <span className="text-sm font-medium">Auto-generate temporary password</span>
                    <p className="text-xs text-gray-500">Patient must change on next login</p>
                  </div>
                </label>
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-50">
                  <input
                    type="radio"
                    name="passwordType"
                    checked={resetPasswordForm.useManualPassword}
                    onChange={() => setResetPasswordForm(prev => ({ ...prev, useManualPassword: true }))}
                    className="w-4 h-4 text-orange-600"
                  />
                  <div>
                    <span className="text-sm font-medium">Set password manually</span>
                    <p className="text-xs text-gray-500">Define a specific password</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Manual Password Input */}
            {resetPasswordForm.useManualPassword && (
              <div className="space-y-2">
                <Label htmlFor="patientNewPassword">New Password</Label>
                <div className="relative">
                  <Input
                    id="patientNewPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter new password (min 8 characters)"
                    value={resetPasswordForm.manualPassword}
                    onChange={(e) => setResetPasswordForm(prev => ({ ...prev, manualPassword: e.target.value }))}
                    className="pr-10"
                    data-testid="patient-manual-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {resetPasswordForm.manualPassword && resetPasswordForm.manualPassword.length < 8 && (
                  <p className="text-xs text-red-500">Password must be at least 8 characters</p>
                )}
              </div>
            )}

            {/* Send Email Option */}
            <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
              <input
                type="checkbox"
                id="patientSendEmail"
                checked={resetPasswordForm.sendEmail}
                onChange={(e) => setResetPasswordForm(prev => ({ ...prev, sendEmail: e.target.checked }))}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <label htmlFor="patientSendEmail" className="text-sm cursor-pointer flex-1">
                <Mail className="h-4 w-4 inline mr-1 text-blue-600" />
                Send email notification with new password
              </label>
            </div>

            {/* Warning */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 inline mr-1" />
              {resetPasswordForm.useManualPassword
                ? "The patient will use this password to login. Make sure to share it securely."
                : "A secure temporary password will be generated. The patient must change it on first login."
              }
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={resettingPassword || (resetPasswordForm.useManualPassword && resetPasswordForm.manualPassword.length < 8)}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="confirm-patient-reset-password-btn"
            >
              {resettingPassword ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  <Key className="h-4 w-4 mr-2" />
                  Reset Password
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default MemberDetailDialog;
